import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { CalendarClock, Lock } from 'lucide-react';
import { isPayableStage, type CapacityCheckInput } from '@tailoring-crm/shared';
import {
  CapacityCheckForm,
  type CapacityStageOption,
} from '../components/capacity/capacity-check-form.js';
import { CapacityResult } from '../components/capacity/capacity-result.js';
import { EmptyState } from '../components/feedback/empty-state.js';
import { PageHeader } from '../components/layout/page-header.js';
import { ListSkeleton } from '@tailoring-crm/ui';
import { isApiRequestError } from '../lib/api/errors.js';
import { useAuth } from '../lib/auth/auth-context.js';
import { useCapacityCheck } from '../lib/capacity/use-capacity.js';
import { useStageConfiguration } from '../lib/workflow/use-kanban.js';

/**
 * "Can we have it by the 24th?" — the question a shop currently answers by feel, asked of the
 * book instead.
 *
 * THE SCREEN DOES NO SCHEDULING. It collects a date and a list of work, converts hours to the
 * minutes the contract carries, and renders what comes back. It walks no calendar, sums no day
 * and decides no verdict; every one of those numbers belongs to the engine, which can see the
 * roster, the leave and the assignments already open, none of which this browser has.
 *
 * The task types are the SHOP'S enabled stages with the SHOP'S labels, narrowed to the payable
 * ones — the stages that are work somebody does, rather than states a garment sits in, which are
 * the only things the contract accepts as a task type.
 *
 * A KARIGAR IS REFUSED. `reports.operations.read` guards the route and this is what "refused"
 * should look like: the reason, in words, instead of a form that 403s when it is pressed.
 */

export function CapacityPage(): ReactElement {
  const { t } = useTranslation();
  const { role } = useAuth();
  const allowed = role !== 'tailor';

  const configuration = useStageConfiguration();
  const [request, setRequest] = useState<CapacityCheckInput | null>(null);
  const check = useCapacityCheck(request, { enabled: allowed });

  if (!allowed) {
    return (
      <>
        <PageHeader title={t('capacity.title')} />
        <EmptyState
          icon={Lock}
          title={t('capacity.noAccessTitle')}
          description={t('capacity.noAccessHint')}
        />
      </>
    );
  }

  const stages: CapacityStageOption[] = (configuration.data?.stages ?? [])
    .filter((stage) => stage.enabled && isPayableStage(stage.key))
    .map((stage) => ({ key: stage.key as CapacityStageOption['key'], label: stage.label }));

  const error = check.error;
  const message =
    error === null
      ? null
      : isApiRequestError(error) || error instanceof Error
        ? error.message
        : t('error.unexpected');

  return (
    <>
      <PageHeader title={t('capacity.title')} description={t('capacity.description')} />

      <div className="flex flex-col gap-6">
        {configuration.isPending ? <ListSkeleton rows={3} /> : null}

        {!configuration.isPending && stages.length === 0 ? (
          <EmptyState
            icon={CalendarClock}
            title={t('capacity.noStagesTitle')}
            description={t('capacity.noStagesHint')}
          />
        ) : null}

        {stages.length > 0 ? (
          <CapacityCheckForm stages={stages} pending={check.isFetching} onCheck={setRequest} />
        ) : null}

        {/* The API's own words, and the form above is left exactly as it was typed so the same
            question can be asked again without retyping it. */}
        {message !== null ? (
          <p
            role="alert"
            className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive"
          >
            {message}
          </p>
        ) : null}

        {request !== null && check.isPending ? <ListSkeleton rows={4} /> : null}

        {request === null ? (
          <EmptyState
            icon={CalendarClock}
            title={t('capacity.empty.title')}
            description={t('capacity.empty.hint')}
          />
        ) : null}

        {check.data ? <CapacityResult result={check.data} /> : null}
      </div>
    </>
  );
}

export default CapacityPage;
